import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth, hasMinRole } from "@/context/AuthContext";
import { Card, Loading, EmptyState } from "@/components/common";
import { MEETING_STATUS, MEETING_TYPE_LABELS, OUTCOME_LABELS, fmtMeetingDate } from "@/lib/meetingsApi";
import { CONSIDERATION_STATE } from "@/lib/forumApi";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft, CalendarDays, MapPin, ListChecks, PlayCircle, Gavel, FileText, Layers, Radio,
  CheckCircle2, Vote, Users, Lock, Unlock, UserCheck,
} from "lucide-react";

const ATT_OPTS = [["present", "Present"], ["absent", "Absent"], ["excused", "Excused"]];
const ATT_SEL = {
  present: "bg-brand-teal text-white border-brand-teal",
  absent: "bg-red-600 text-white border-red-600",
  excused: "bg-brand-gold text-[#5c4405] border-brand-gold",
};

export default function MeetingWorkspace() {
  const { id } = useParams();
  const { user } = useAuth();
  const isClerk = hasMinRole(user?.role, "manager");
  const [meeting, setMeeting] = useState(null);
  const [agenda, setAgenda] = useState([]);
  const [pubs, setPubs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [resultCons, setResultCons] = useState(null);
  const [cons, setCons] = useState(null);     // consideration for current item
  const [progress, setProgress] = useState(null);
  const [confirm, setConfirm] = useState(null); // 'open' | 'close'
  const [rollCall, setRollCall] = useState(null);
  const [confirmComplete, setConfirmComplete] = useState(false);
  const [err, setErr] = useState(false);

  const load = useCallback(async () => {
    try {
      const [m, a, p] = await Promise.all([
        api.get(`/meetings/${id}`),
        api.get(`/meetings/${id}/agenda`),
        api.get(`/meetings/${id}/publications`).catch(() => ({ data: { publications: [] } })),
      ]);
      setMeeting(m.data); setAgenda(a.data.agenda); setPubs(p.data.publications);
    } catch { setErr(true); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const loadRoll = useCallback(async () => {
    if (!isClerk) return;
    try { const r = await api.get(`/meetings/${id}/roll-call`); setRollCall(r.data); } catch { /* */ }
  }, [id, isClerk]);
  useEffect(() => { if (meeting && ["published", "in_progress"].includes(meeting.status)) loadRoll(); }, [loadRoll, meeting]);

  const rollAction = async (fn, ok) => {
    try { const r = await fn(); setRollCall((s) => ({ ...s, ...r.data })); if (ok) toast.success(ok); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const startRoll = () => rollAction(() => api.post(`/meetings/${id}/roll-call/start`), "Roll call started");
  const markAll = () => rollAction(() => api.post(`/meetings/${id}/attendance/mark-all-present`));
  const setAtt = (bmid, status) => rollAction(() => api.patch(`/meetings/${id}/attendance/${bmid}`, { status }));
  const completeRoll = async () => {
    setConfirmComplete(false);
    try { const r = await api.post(`/meetings/${id}/roll-call/complete`); setRollCall((s) => ({ ...s, roll_call_status: "completed", quorum: r.data.quorum })); toast.success("Roll call complete"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // live consideration + progress for the current item
  const loadLive = useCallback(async () => {
    if (!meeting || meeting.status !== "in_progress" || !meeting.current_item_id) { setCons(null); setProgress(null); return; }
    try {
      const c = await api.get(`/agenda-items/${meeting.current_item_id}/consideration`);
      setCons(c.data.consideration);
      if (c.data.consideration) {
        const pg = await api.get(`/considerations/${c.data.consideration.consideration_id}/progress`);
        setProgress(pg.data);
      } else setProgress(null);
    } catch { /* ignore */ }
  }, [meeting]);
  useEffect(() => { loadLive(); const t = setInterval(loadLive, 5000); return () => clearInterval(t); }, [loadLive]);

  if (err) return <EmptyState icon={CalendarDays} title="Meeting not found" description="It may have been removed or you lack access." />;
  if (!meeting) return <Loading label="Loading meeting…" />;

  const status = MEETING_STATUS[meeting.status] || { label: meeting.status };
  const allItems = agenda.flatMap((g) => g.items);
  const inProgress = meeting.status === "in_progress";

  const act = async (fn, ok) => { setBusy(true); try { await fn(); if (ok) toast.success(ok); await load(); await loadLive(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); } };
  const start = () => act(() => api.post(`/meetings/${id}/start`), "Meeting started");
  const complete = () => act(() => api.post(`/meetings/${id}/complete`));
  const discuss = (item_id) => act(() => api.post(`/agenda-items/${item_id}/discuss`), "Now discussing");
  const openVoting = () => { setConfirm(null); act(() => api.post(`/considerations/${cons.consideration_id}/open-voting`), "Voting opened"); };
  const closeVoting = () => { setConfirm(null); act(() => api.post(`/considerations/${cons.consideration_id}/close-voting`), "Voting closed"); };

  return (
    <div data-testid="meeting-workspace">
      <Link to="/meetings" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-brand-navy mb-3">
        <ArrowLeft className="h-4 w-4" /> All Meetings
      </Link>
      <Card className="mb-5" data-testid="meeting-header">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-brand-civic">{meeting.governing_body_name}</span>
              <span data-testid="meeting-status" className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${status.cls}`}>{status.label}</span>
            </div>
            <h1 className="font-display text-2xl font-extrabold text-brand-ink mt-1">{meeting.title}</h1>
            <p className="text-sm text-slate-500 mt-1 flex flex-wrap items-center gap-x-4 gap-y-1">
              <span className="flex items-center gap-1.5"><CalendarDays className="h-4 w-4" /> {fmtMeetingDate(meeting.date, meeting.start_time)}</span>
              {(meeting.location || meeting.virtual_url) && <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4" /> {meeting.location || meeting.virtual_url}</span>}
              <span>{MEETING_TYPE_LABELS[meeting.meeting_type] || meeting.meeting_type}</span>
            </p>
          </div>
          {isClerk && (
            <div className="flex gap-2">
              {["draft", "scheduled", "published"].includes(meeting.status) && (
                <Link to={`/meetings/${id}/agenda`} data-testid="open-agenda-builder"
                  className="inline-flex items-center gap-1.5 rounded-lg border border-brand-line px-3 py-2 text-sm font-semibold text-brand-navy hover:bg-brand-bg">
                  <ListChecks className="h-4 w-4" /> Agenda Builder
                </Link>
              )}
              {["scheduled", "published"].includes(meeting.status) && (
                <button data-testid="start-meeting-btn" disabled={busy} onClick={start}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-brand-navy px-4 py-2 text-sm font-semibold text-white hover:bg-brand-navy-900 disabled:opacity-40">
                  <PlayCircle className="h-4 w-4" /> Start Meeting
                </button>
              )}
              {inProgress && (
                <button data-testid="complete-meeting-btn" disabled={busy} onClick={complete}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-brand-teal px-4 py-2 text-sm font-semibold text-white hover:bg-brand-teal/90 disabled:opacity-40">
                  <CheckCircle2 className="h-4 w-4" /> Complete
                </button>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* Meeting progress strip — derived from authoritative meeting state */}
      <ProgressStrip meeting={meeting} rollCall={rollCall} />

      {meeting.status === "completed" && (
        <Card className="mb-5 border-brand-teal/40 bg-brand-teal-50" data-testid="meeting-complete-actions">
          <div className="flex flex-wrap items-center gap-3">
            <CheckCircle2 className="h-5 w-5 text-brand-teal" />
            <div className="flex-1">
              <div className="font-display font-bold text-brand-ink">Meeting Complete</div>
              <div className="text-sm text-slate-500">This is the official historical record for this meeting.</div>
            </div>
            <Link to={`/meetings/${id}/summary`} data-testid="view-summary-btn" className="inline-flex items-center gap-1.5 rounded-lg bg-brand-navy px-4 py-2 text-sm font-semibold text-white hover:bg-brand-navy-900"><FileText className="h-4 w-4" /> View Summary</Link>
            <a href={`${api.defaults.baseURL}/meetings/${id}/summary.pdf`} target="_blank" rel="noreferrer" data-testid="download-minutes-btn" className="inline-flex items-center gap-1.5 rounded-lg border border-brand-line bg-white px-4 py-2 text-sm font-semibold text-brand-navy hover:bg-brand-bg"><FileText className="h-4 w-4" /> Download Minutes</a>
            <a href={`/public/meetings/${meeting.meeting_id}`} target="_blank" rel="noreferrer" data-testid="view-public-record-btn" className="inline-flex items-center gap-1.5 rounded-lg border border-brand-line bg-white px-4 py-2 text-sm font-semibold text-brand-navy hover:bg-brand-bg">View Public Record</a>
          </div>
        </Card>
      )}

      {/* Roll Call + Quorum (Clerk) */}
      {isClerk && ["published", "in_progress"].includes(meeting.status) && rollCall && (
        <Card className="mb-5" data-testid="roll-call-panel">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <UserCheck className="h-4 w-4 text-brand-civic" />
            <h2 className="font-display font-bold text-brand-ink text-base">Roll Call</h2>
            {rollCall.quorum && (
              <span data-testid="quorum-badge" className={`ml-auto inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-bold ${rollCall.quorum.met ? "bg-brand-teal-50 text-brand-teal border-brand-teal/40" : "bg-red-50 text-red-600 border-red-200"}`}>
                {rollCall.quorum.met ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Users className="h-3.5 w-3.5" />}
                Quorum {rollCall.quorum.met ? "Established" : "Not Established"} · {rollCall.quorum.present_voting}/{rollCall.quorum.eligible_voting} present · {rollCall.quorum.required} required
              </span>
            )}
          </div>
          {rollCall.roll_call_status === "not_started" ? (
            <button data-testid="start-roll-call-btn" onClick={startRoll}
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand-navy px-4 py-2 text-sm font-semibold text-white hover:bg-brand-navy-900">
              <UserCheck className="h-4 w-4" /> Start Roll Call
            </button>
          ) : (
            <>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">{rollCall.members?.length || 0} Voting Members</span>
                <button data-testid="mark-all-present-btn" onClick={markAll} className="rounded-lg border border-brand-line px-3 py-1.5 text-sm font-semibold text-brand-navy hover:bg-brand-bg">Mark All Present</button>
              </div>
              <div className="space-y-1.5">
                {rollCall.members?.map((mem) => (
                  <div key={mem.board_member_id} data-testid="roll-call-member" className="flex items-center gap-2 rounded-lg border border-brand-line px-3 py-2">
                    <span className="flex-1 text-sm text-brand-ink">{mem.name} <span className="text-xs text-slate-400">· {mem.role_label}{!mem.voting_eligible && " · non-voting"}</span></span>
                    {ATT_OPTS.map(([v, lbl]) => (
                      <button key={v} data-testid={`attendance-${v}-${mem.board_member_id}`} onClick={() => setAtt(mem.board_member_id, v)}
                        className={`rounded-md px-2.5 py-1 text-xs font-semibold border ${mem.attendance_status === v ? ATT_SEL[v] : "border-brand-line text-slate-500 hover:bg-brand-bg"}`}>{lbl}</button>
                    ))}
                  </div>
                ))}
              </div>
              {rollCall.roll_call_status !== "completed" && (
                <button data-testid="complete-roll-call-btn" onClick={() => setConfirmComplete(true)}
                  className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-brand-teal px-4 py-2 text-sm font-semibold text-white hover:bg-brand-teal/90">
                  <CheckCircle2 className="h-4 w-4" /> Complete Roll Call
                </button>
              )}
              {rollCall.roll_call_status === "completed" && <span className="mt-3 inline-block text-xs font-bold uppercase tracking-wider text-brand-teal">Roll Call Completed</span>}
            </>
          )}
        </Card>
      )}

      {/* Live voting controls for the current item */}
      {inProgress && isClerk && cons && (
        <Card className="mb-5 border-brand-gold/50 bg-brand-gold-50/40" data-testid="live-controls">
          <div className="flex items-center gap-2 mb-3">
            <Vote className="h-4 w-4 text-[#9A7212]" />
            <h2 className="font-display font-bold text-brand-ink text-base">Live Voting — Current Item</h2>
            <span className={`ml-auto inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${(CONSIDERATION_STATE[cons.status] || {}).cls || ""}`}>
              {(CONSIDERATION_STATE[cons.status] || {}).label || cons.status}
            </span>
          </div>
          {progress && (
            <div className="flex items-center gap-4 mb-3 text-sm">
              <span className="inline-flex items-center gap-1.5 text-slate-600"><Users className="h-4 w-4" /> {progress.eligible} Eligible</span>
              <span className="font-semibold text-brand-teal">{progress.submitted} Submitted</span>
              <span className="font-semibold text-[#9A7212]">{progress.pending} Pending</span>
            </div>
          )}
          {progress?.members?.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 mb-3" data-testid="member-progress">
              {progress.members.map((mem) => (
                <div key={mem.user_id} className="flex items-center justify-between rounded-lg border border-brand-line bg-white px-3 py-1.5 text-sm">
                  <span className="text-brand-ink">{mem.name}</span>
                  {mem.vote_value
                    ? <span className="font-semibold text-brand-navy">{mem.vote_value}</span>
                    : <span className={mem.submitted ? "text-brand-teal font-semibold" : "text-slate-400"}>{mem.submitted ? "Submitted" : "Pending"}</span>}
                </div>
              ))}
            </div>
          )}
          {cons.status === "voting_closed" && cons.vote_totals && (
            <div className="mb-3 text-sm text-slate-700">Totals — YES {cons.vote_totals.YES} · NO {cons.vote_totals.NO} · WITHHELD {cons.vote_totals.WITHHELD}</div>
          )}
          <div className="flex flex-wrap gap-2">
            {cons.status === "under_discussion" && (
              <button data-testid="open-voting-btn" disabled={busy} onClick={() => setConfirm("open")}
                className="inline-flex items-center gap-1.5 rounded-lg bg-brand-navy px-4 py-2 text-sm font-semibold text-white hover:bg-brand-navy-900 disabled:opacity-40">
                <Unlock className="h-4 w-4" /> Open Voting
              </button>
            )}
            {cons.status === "voting_open" && (
              <button data-testid="close-voting-btn" disabled={busy} onClick={() => setConfirm("close")}
                className="inline-flex items-center gap-1.5 rounded-lg bg-brand-gold px-4 py-2 text-sm font-semibold text-[#5c4405] hover:opacity-90 disabled:opacity-40">
                <Lock className="h-4 w-4" /> Close Voting
              </button>
            )}
            {cons.status === "voting_closed" && (
              <button data-testid="confirm-result-btn" disabled={busy} onClick={() => setResultCons(cons)}
                className="inline-flex items-center gap-1.5 rounded-lg bg-brand-teal px-4 py-2 text-sm font-semibold text-white hover:bg-brand-teal/90 disabled:opacity-40">
                <Gavel className="h-4 w-4" /> Confirm Official Result
              </button>
            )}
          </div>
        </Card>
      )}

      <div className="grid gap-5 lg:grid-cols-3">
        <Card className="lg:col-span-2" data-testid="meeting-agenda">
          <div className="flex items-center gap-2 mb-3"><Layers className="h-4 w-4 text-brand-civic" /><h2 className="font-display font-bold text-brand-ink text-base">Agenda</h2></div>
          {allItems.length === 0
            ? <p className="text-sm text-slate-400">No agenda items. {isClerk && <Link to={`/meetings/${id}/agenda`} className="text-brand-civic font-semibold">Open the Agenda Builder.</Link>}</p>
            : agenda.filter((g) => g.items.length > 0).map((grp) => (
              <div key={grp.category.key} className="mb-4">
                <div className="text-xs font-bold uppercase tracking-wider text-brand-civic mb-1.5">{grp.category.label}</div>
                <div className="space-y-1.5">
                  {grp.items.map((it) => {
                    const isCurrent = meeting.current_item_id === it.item_id;
                    return (
                      <div key={it.item_id} data-testid="meeting-agenda-item"
                        className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${isCurrent ? "border-brand-gold bg-brand-gold-50" : "border-brand-line"}`}>
                        <span className="text-xs font-bold text-brand-navy w-8">{it.number}</span>
                        <Link to={`/insights/${it.insight_id}`} className="flex-1 text-sm text-brand-ink hover:text-brand-civic truncate">{it.insight_title}</Link>
                        {it.outcome
                          ? <span data-testid="item-outcome" className="inline-flex rounded-full border border-brand-teal/30 bg-brand-teal-50 px-2 py-0.5 text-[10px] font-semibold text-brand-teal">{OUTCOME_LABELS[it.outcome] || it.outcome}</span>
                          : inProgress && isClerk && !isCurrent && (
                            <button data-testid="discuss-item-btn" onClick={() => discuss(it.item_id)} disabled={busy}
                              className="inline-flex items-center gap-1 rounded-md bg-brand-navy text-white px-2 py-1 text-xs font-semibold hover:bg-brand-navy-900 disabled:opacity-40"><Radio className="h-3 w-3" /> Make Current</button>
                          )}
                        {isCurrent && !it.outcome && <span className="inline-flex items-center gap-1 rounded-full bg-brand-gold px-2 py-0.5 text-[10px] font-bold uppercase text-[#5c4405]"><Radio className="h-3 w-3" /> Current</span>}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
        </Card>

        <Card data-testid="meeting-publications">
          <div className="flex items-center gap-2 mb-3"><FileText className="h-4 w-4 text-brand-civic" /><h2 className="font-display font-bold text-brand-ink text-base">Publications</h2></div>
          {pubs.length === 0 ? <p className="text-sm text-slate-400">Not published yet.</p> : (
            <div className="space-y-2">
              {pubs.map((p) => (
                <div key={p.publication_id} data-testid="publication-item" className="rounded-lg border border-brand-line px-3 py-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-brand-ink">Version {p.version}{meeting.current_publication === p.version && <span className="ml-1.5 text-[10px] font-bold uppercase text-brand-teal">current</span>}</span>
                  </div>
                  <div className="text-xs text-slate-400">{p.published_by_name} · {fmtMeetingDate(p.created_at?.slice(0, 10))}</div>
                  <div className="flex gap-3 mt-1 text-xs font-semibold">
                    <a className="text-brand-navy hover:underline" href={`${api.defaults.baseURL}/public/publications/${p.publication_id}/packet.pdf`} target="_blank" rel="noreferrer" data-testid="staff-packet-pdf">Packet PDF</a>
                    <a className="text-brand-navy hover:underline" href={`/public/meetings/${meeting.meeting_id}`} target="_blank" rel="noreferrer" data-testid="staff-view-public">View Public Agenda</a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {confirm === "open" && (
        <ConfirmDialog testid="open-voting-dialog" title="Open Voting?" confirmLabel="Open Voting" busy={busy} onCancel={() => setConfirm(null)} onConfirm={openVoting}>
          Board Members assigned to <strong>{meeting.governing_body_name}</strong> will be able to submit their vote for this agenda item.
        </ConfirmDialog>
      )}
      {confirm === "close" && (
        <ConfirmDialog testid="close-voting-dialog" title="Close Voting?" confirmLabel="Close Voting" busy={busy} onCancel={() => setConfirm(null)} onConfirm={closeVoting}>
          {progress ? <><strong>{progress.submitted} of {progress.eligible}</strong> eligible members have submitted a vote.{progress.pending > 0 && ` ${progress.pending} member${progress.pending === 1 ? "" : "s"} have not voted.`}<br /></> : null}
          Once closed, Board Members will no longer be able to submit or change their vote.
        </ConfirmDialog>
      )}
      {resultCons && <ResultDialog cons={resultCons} onClose={() => setResultCons(null)} onDone={async () => { setResultCons(null); await load(); await loadLive(); }} />}
      {confirmComplete && rollCall && (
        <ConfirmDialog testid="complete-roll-call-dialog" title="Complete Roll Call?" confirmLabel="Complete Roll Call" busy={false} onCancel={() => setConfirmComplete(false)} onConfirm={completeRoll}>
          {(() => { const p = (rollCall.members || []).filter((x) => x.attendance_status === "present").length; const a = (rollCall.members || []).length - p; return <>
            <strong>{rollCall.members?.length || 0} Members</strong> · {p} Present · {a} Absent/Excused.<br />
            Quorum Required: <strong>{rollCall.quorum?.required}</strong> — Quorum: <strong className={rollCall.quorum?.met ? "text-brand-teal" : "text-red-600"}>{rollCall.quorum?.met ? "MET" : "NOT MET"}</strong>.
          </>; })()}
        </ConfirmDialog>
      )}
    </div>
  );
}

function ProgressStrip({ meeting, rollCall }) {
  const rc = meeting.roll_call_status || "not_started";
  const q = rollCall?.quorum || meeting.quorum_snapshot;
  const steps = [
    { key: "agenda", label: "Agenda", state: meeting.current_publication ? "done" : "todo", sub: meeting.current_publication ? "Published" : "Not Published" },
    { key: "rollcall", label: "Roll Call", state: rc === "completed" ? "done" : rc === "in_progress" ? "active" : "todo", sub: rc === "completed" ? "Complete" : rc === "in_progress" ? "In Progress" : "Not Started" },
    { key: "quorum", label: "Quorum", state: q ? (q.met ? "done" : "warn") : "todo", sub: q ? (q.met ? "Established" : "Not Established") : "Pending" },
    { key: "meeting", label: "Meeting", state: meeting.status === "completed" ? "done" : meeting.status === "in_progress" ? "active" : "todo", sub: meeting.status === "in_progress" ? "In Progress" : meeting.status === "completed" ? "Complete" : "Not Started" },
    { key: "complete", label: "Complete", state: meeting.status === "completed" ? "done" : "todo", sub: meeting.status === "completed" ? "Completed" : "Pending" },
  ];
  const blocked = q && !q.met && meeting.status !== "completed";
  return (
    <Card className="mb-5" data-testid="progress-strip">
      <div className="flex items-center gap-1 overflow-x-auto">
        {steps.map((s, i) => (
          <React.Fragment key={s.key}>
            <div className="flex items-center gap-2 px-2 shrink-0" data-testid={`progress-step-${s.key}`} data-state={s.state}>
              {s.state === "done" ? <CheckCircle2 className="h-5 w-5 text-brand-teal" />
                : s.state === "active" ? <Radio className="h-5 w-5 text-brand-gold" />
                  : s.state === "warn" ? <Users className="h-5 w-5 text-red-500" />
                    : <span className="h-5 w-5 rounded-full border-2 border-slate-300 inline-block" />}
              <div className="leading-tight">
                <div className={`text-sm font-bold ${s.state === "warn" ? "text-red-600" : s.state === "todo" ? "text-slate-400" : "text-brand-ink"}`}>{s.label}</div>
                <div className="text-[11px] text-slate-400">{s.sub}</div>
              </div>
            </div>
            {i < steps.length - 1 && <div className="h-px w-6 bg-brand-line shrink-0" />}
          </React.Fragment>
        ))}
      </div>
      {blocked && (
        <div data-testid="progress-blocker" className="mt-3 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <Users className="h-4 w-4 mt-0.5 shrink-0" />
          <span>Meeting business is locked — {q.required} voting members are required, {q.present_voting} present. Establish quorum in Roll Call to proceed.</span>
        </div>
      )}
    </Card>
  );
}

function ConfirmDialog({ testid, title, confirmLabel, busy, children, onCancel, onConfirm }) {
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent data-testid={testid}>
        <DialogHeader><DialogTitle>{title}</DialogTitle>
          <DialogDescription>Please review the details below before confirming.</DialogDescription>
        </DialogHeader>
        <p className="text-sm text-slate-600 leading-relaxed">{children}</p>
        <DialogFooter>
          <button onClick={onCancel} className="rounded-lg border border-brand-line px-4 py-2 text-sm font-medium text-slate-600 hover:bg-brand-bg">Cancel</button>
          <button data-testid={`${testid}-confirm`} disabled={busy} onClick={onConfirm} className="rounded-lg bg-brand-navy px-4 py-2 text-sm font-semibold text-white hover:bg-brand-navy-900 disabled:opacity-40">{confirmLabel}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResultDialog({ cons, onClose, onDone }) {
  const [f, setF] = useState({ outcome: "approved", motion: "", mover: "", second: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    setBusy(true);
    try { await api.post(`/considerations/${cons.consideration_id}/result`, f); toast.success("Official result confirmed"); onDone(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(false); }
  };
  const inp = "w-full h-10 rounded-lg border border-brand-line px-3 text-sm outline-none focus:border-brand-civic";
  const t = cons.vote_totals || {};
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="result-dialog">
        <DialogHeader><DialogTitle>Confirm Official Result</DialogTitle>
          <DialogDescription>Record the governing body's official disposition for this item.</DialogDescription>
        </DialogHeader>
        <div className="rounded-lg border border-brand-line bg-brand-bg p-3 text-sm text-slate-700 mb-1">
          Vote totals — <strong>YES {t.YES ?? 0}</strong> · <strong>NO {t.NO ?? 0}</strong> · <strong>WITHHELD {t.WITHHELD ?? 0}</strong>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-sm font-semibold text-brand-ink mb-1 block">Official Disposition</label>
            <select data-testid="result-outcome-select" value={f.outcome} onChange={(e) => set("outcome", e.target.value)} className={inp}>
              {Object.entries(OUTCOME_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input data-testid="result-mover" value={f.mover} onChange={(e) => set("mover", e.target.value)} placeholder="Mover" className={inp} />
            <input data-testid="result-second" value={f.second} onChange={(e) => set("second", e.target.value)} placeholder="Second" className={inp} />
          </div>
          <input data-testid="result-motion" value={f.motion} onChange={(e) => set("motion", e.target.value)} placeholder="Motion (optional)" className={inp} />
        </div>
        <DialogFooter>
          <button onClick={onClose} className="rounded-lg border border-brand-line px-4 py-2 text-sm font-medium text-slate-600 hover:bg-brand-bg">Cancel</button>
          <button data-testid="result-save" disabled={busy} onClick={save} className="rounded-lg bg-brand-teal px-4 py-2 text-sm font-semibold text-white hover:bg-brand-teal/90 disabled:opacity-40">Confirm Result</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
